<script setup lang="ts">
import { computed } from 'vue';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';
import {
  StayTag,
  HotelStayTagType,
} from '@apps/main/new-desktop/modules/stayTag';

import { AppLinkButton, AppStars, AppTag } from '@libs/ui';

import type {
  AccommodationProductCardContent,
  AccommodationSegmentContext,
} from '../../../models';

import AccommodationAmenities from './AccommodationAmenities.vue';

interface AccommodationProductCardContentProps {
  productCardContent: AccommodationProductCardContent;
  context: AccommodationSegmentContext;
  printMode: boolean;
}

const props = defineProps<AccommodationProductCardContentProps>();

interface AccommodationProductCardContentEmits {
  (eventName: 'mouse-enter-content'): void;
  (eventName: 'mouse-leave-content'): void;
  (eventName: 'show-details'): void;
  (eventName: 'show-rooms'): void;
}

const emit = defineEmits<AccommodationProductCardContentEmits>();

const browserStore = useBrowserStore();

const hasMultipleRooms = computed<boolean>(
  () => props.productCardContent.cardDetails.length > 1
);

function onEditRoomsBannerClick(): void {
  if (browserStore.isSmallDevice) {
    emit('show-rooms');
  }
}
</script>

<template>
  <div
    :class="[
      'accommodation-product-card-content',
      { 'print-mode': props.printMode },
    ]"
    @mouseenter="emit('mouse-enter-content')"
    @mouseleave="emit('mouse-leave-content')"
    @click="emit('show-details')"
  >
    <div class="details">
      <div class="upper-section">
        <div class="name">
          {{ props.productCardContent.header }}
        </div>

        <div class="location">{{ props.productCardContent.subheader }}</div>

        <div class="info-wrapper">
          <AppStars :amount="props.productCardContent.rating.stars" size="18" />
          <StayTag
            v-if="
              props.productCardContent.cancellationPolicies &&
              props.productCardContent.cancellationPolicies.type ===
                HotelStayTagType.FreeCancellation
            "
            class="cancellation-policy"
            :stay-tag="props.productCardContent.cancellationPolicies"
            :wide="browserStore.isSmallDevice"
            short-version
          />
          <AppTag v-if="props.productCardContent.mealName" type="grey" size="s">
            <template #title>
              <span class="tag-text">
                {{ props.productCardContent.mealName }}
              </span>
            </template>
          </AppTag>
        </div>

        <AccommodationAmenities
          :amenities="props.productCardContent.amenities"
        />
      </div>
      <div
        class="middle-section"
        @click.stop="onEditRoomsBannerClick"
        @mouseenter="emit('mouse-leave-content')"
        @mouseleave="emit('mouse-enter-content')"
      >
        <div
          v-for="element in props.productCardContent.cardDetails"
          :key="element.label + element.description"
          class="row"
        >
          <div :class="['description', { 'single-room': !hasMultipleRooms }]">
            <span v-if="hasMultipleRooms" class="label">
              {{ element.label }}:
            </span>
            {{ element.description }}
          </div>
        </div>
        <AppLinkButton
          v-if="!props.printMode"
          :text="$t('planner.edit_room')"
          size="small"
          @click="emit('show-rooms')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.accommodation-product-card-content {
  height: 100%;

  &.print-mode {
    .details {
      .upper-section {
        .name {
          @include on-hover {
            cursor: default;
            text-decoration: none;
          }
        }
      }
    }
  }

  @include for-small-devices-max {
    grid-template-rows: auto auto auto;
    grid-template-columns: auto;
  }

  .details {
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    height: 100%;

    .upper-section {
      padding: var(--spacing-16) var(--spacing-12);

      .name {
        padding: 0;
        margin-bottom: var(--spacing-4);
        @include font-label-xl-bold;
        color: var(--color-primary-20);
        text-decoration: none;

        @include on-hover {
          cursor: pointer;
          text-decoration: underline;
        }
      }

      .location {
        @include font-label-s-light;
        margin-bottom: var(--spacing-4);
        color: var(--color-secondary-60);
      }

      .info-wrapper {
        display: flex;
        flex-direction: row;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--spacing-8);
        margin-bottom: var(--spacing-16);
        @include for-small-devices-max {
          flex-direction: column;
          align-items: start;
          gap: var(--spacing-12);
        }

        .cancellation-policy {
          width: fit-content;
        }
      }

      .tag-text {
        @include font-label-s-bold;
      }
    }

    .middle-section {
      padding: var(--spacing-16);
      background: var(--color-secondary-97);
      border: 1px solid var(--color-secondary-93);
      border-radius: var(--border-radius-8);
      margin: auto var(--spacing-12) var(--spacing-16) var(--spacing-12);

      .row {
        display: flex;
        gap: var(--spacing-4);
        margin-bottom: var(--spacing-8);

        .description {
          @include font-label-s-light;

          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          width: 0;
          min-width: 100%;

          .label {
            @include font-label-s-bold;
            white-space: nowrap;
          }

          &.single-room {
            white-space: normal;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            line-height: var(--line-heights-14);
          }
        }
      }
    }
  }

  .price {
    align-self: center;
  }
}
</style>
